import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { TranslocoModule } from '@ngneat/transloco';
import { environment } from 'environments/environment';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { format } from 'sql-formatter';
import Prism from 'prismjs';
import 'prismjs/components/prism-sql';
import { PublicFunctionsService } from 'app/mock-api/common/services/functions.service';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';

/** Max rows shown in the side table preview (no pagination). */
export const FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS = 100;

export interface FormulaPromptDialogData {
    metricName: string;
    kpiId: number | string;
    isExternal?: boolean;
    tableId?: number | string;
    externalTableId?: number | string;
    currentLevel?: number | string;
    currentCalculation?: string;
    currentDescription?: string;
    currentColumnsUsed?: string[];
    lang?: string;
    /** Column headers for the read-only data preview (right pane). */
    previewHeaders?: string[];
    /** Up to {@link FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS} data rows. */
    previewRows?: any[][];
}

interface KpiLogicItem {
    id?: number | string;
    level?: number | string;
    kpi_name: string;
    calculation: string;
    columns_used?: string[];
    description: string;
}

interface KpiValidationPreview {
    sql: string;
    rows: Record<string, unknown>[];
}

interface GeneratedKpiPayload {
    level: number;
    kpi_name: string;
    calculation: string;
    columns_used: string[];
    description: string;
}

@Component({
    selector: 'app-formula-prompt-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SaiDialogHeaderComponent,
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        ClipboardModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        TranslocoModule,
        FuseAlertComponent,
        CopyOnDblclickDirective,
    ],
    templateUrl: './formula-prompt-dialog.component.html',
    styleUrl: './formula-prompt-dialog.component.scss',
})
export class FormulaPromptDialogComponent {
    promptCtrl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
    });

    isGenerating = false;
    isSaving = false;
    isValidating = false;
    showAlert = false;
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    oldKpi: KpiLogicItem | null = null;
    generatedKpi: KpiLogicItem | null = null;
    validationPreview: KpiValidationPreview | null = null;
    highlightedValidationSql: SafeHtml = '';

    /** Read-only table preview (max 100 rows), passed from parent. */
    readonly previewHeaders: string[];
    readonly previewRows: any[][];
    /** Pre-formatted preview rows to avoid per-cell function calls in template. */
    readonly formattedPreviewRows: string[][];

    get hasGeneratedKpi(): boolean {
        return !!this.generatedKpi;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: FormulaPromptDialogData,
        private dialogRef: MatDialogRef<FormulaPromptDialogComponent>,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        private publicFunctions: PublicFunctionsService
    ) {
        // Show current KPI values immediately when dialog opens.
        this.oldKpi = {
            id: data?.kpiId,
            level: data?.currentLevel,
            kpi_name: String(data?.metricName ?? ''),
            calculation: String(data?.currentCalculation ?? ''),
            columns_used: Array.isArray(data?.currentColumnsUsed)
                ? data.currentColumnsUsed.map(v => String(v))
                : [],
            description: String(data?.currentDescription ?? ''),
        };
        this.previewHeaders = Array.isArray(data?.previewHeaders)
            ? data.previewHeaders.map(h => String(h))
            : [];
        this.previewRows = Array.isArray(data?.previewRows)
            ? data.previewRows.slice(0, FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS)
            : [];
        this.formattedPreviewRows = this.previewRows.map(row =>
            row.map((cell: unknown) =>
                this.publicFunctions.formatPreviewNumericCell(cell, 4)
            )
        );
    }

    trackByHeaderIndex(index: number): number {
        return index;
    }

    trackByRowIndex(index: number): number {
        return index;
    }

    formatPreviewCellValue(value: unknown): string {
        return this.publicFunctions.formatPreviewNumericCell(value, 4);
    }

    close(): void {
        this.dialogRef.close();
    }

    private mapKpiItem(item: any): KpiLogicItem | null {
            if (!item || typeof item !== 'object') return null;

            const id = item?.id ?? item?.kpi_id ?? item?.kpiId ?? undefined;
            const level =
                item?.level ??
                item?.kpi_level ??
                item?.kpiLevel ??
                undefined;
            const kpi_name =
                item?.kpi_name ??
                item?.kpiName ??
                item?.name ??
                '';
            const calculation =
                item?.calculation ??
                item?.formula ??
                item?.expression ??
                '';
            const description =
                item?.description ??
                item?.desc ??
                item?.about ??
                '';
            const columns_used = Array.isArray(item?.columns_used)
                ? item.columns_used.map((v: any) => String(v))
                : [];

            if (
                (kpi_name ?? '') === '' &&
                (calculation ?? '') === '' &&
                (description ?? '') === ''
            ) {
                return null;
            }

            return {
                id,
                level,
                kpi_name: String(kpi_name),
                calculation: String(calculation),
                columns_used,
                description: String(description),
            } satisfies KpiLogicItem;
    }

    private extractGeneratedPair(response: any): {
        oldKpi: KpiLogicItem;
        generatedKpi: KpiLogicItem;
    } | null {
        const oldRaw = response?.data?.old_kpi ?? response?.data?.data?.old_kpi;
        const generatedRaw =
            response?.data?.generated_kpi ?? response?.data?.data?.generated_kpi;

        const oldKpi = this.mapKpiItem(oldRaw);
        const generatedKpi = this.mapKpiItem(generatedRaw);
        if (!oldKpi || !generatedKpi) return null;
        return { oldKpi, generatedKpi };
    }

    private extractGeneratedOnly(response: any): KpiLogicItem | null {
        const candidates = [
            response?.data?.generated_kpi,
            response?.data?.data?.generated_kpi,
            response?.data?.data,
            response?.data,
        ];
        for (const c of candidates) {
            const mapped = this.mapKpiItem(c);
            if (mapped) return mapped;
        }
        return null;
    }

    private extractSavedKpi(response: any): KpiLogicItem[] | null {
        const mapItem = (item: any): KpiLogicItem | null => this.mapKpiItem(item);

        // Your API response (example) looks like:
        // { success: true, data: { data: { id, kpi_name, calculation, description, ... } } }
        const directItem = response?.data?.data;
        if (!Array.isArray(directItem)) {
            const mapped = mapItem(directItem);
            if (mapped) return [mapped];
        }

        const arrays: any[] = [];
        const pushIfArray = (v: any) => {
            if (Array.isArray(v)) arrays.push(v);
        };

        pushIfArray(response?.data?.kpiLogic);
        pushIfArray(response?.data?.kpi_logic);
        pushIfArray(response?.data?.kpiLogicItems);
        pushIfArray(response?.data?.coreLogic);
        pushIfArray(response?.data?.core_logic);
        pushIfArray(response?.kpiLogic);
        pushIfArray(response?.kpi_logic);

        // Sometimes the array is nested under the `data` object.
        pushIfArray(response?.data?.data?.kpiLogic);
        pushIfArray(response?.data?.data?.coreLogic);

        if (arrays.length === 0) return null;

        const raw = arrays[0];
        return raw
            .map((item: any) => mapItem(item))
            .filter((v: KpiLogicItem | null): v is KpiLogicItem => !!v);
    }

    private getErrorMessage(error: any): string {
        try {
            const msg = error?.error ?? error?.message;
            if (typeof msg === 'string') {
                // Sometimes backend returns a JSON string in `error.error`.
                return JSON.parse(msg)?.message ?? msg;
            }
            return error?.error?.message ?? error?.message ?? 'Request failed';
        } catch {
            return error?.error?.message ?? error?.message ?? 'Request failed';
        }
    }

    private setInlineAlert(type: FuseAlertType, message: string): void {
        this.alert = { type, message };
        this.showAlert = true;
        this.cdr.markForCheck();
    }

    private formatSqlSafely(sql: string): string {
        const cleanSql = String(sql ?? '').trim();
        if (!cleanSql) return '';

        // Try dialects that support casts like ::INTEGER first.
        const dialects: string[] = ['postgresql', 'sql'];
        for (const language of dialects) {
            try {
                return format(cleanSql, {
                    language: language as any,
                    tabWidth: 2,
                    useTabs: false,
                    keywordCase: 'upper',
                }).replace(/^\s+/, '');
            } catch {
                // Try next dialect.
            }
        }

        // If formatter can't parse, still show the original SQL.
        return cleanSql.replace(/^\s+/, '');
    }

    onSqlCopied(copied: boolean): void {
        if (!copied) return;
        this.snackBar.open('SQL query copied', 'Close', {
            duration: 2000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
        });
    }

    closeInlineAlert(): void {
        this.showAlert = false;
        this.cdr.markForCheck();
    }

    async submit(): Promise<void> {
        await this.generate();
    }

    async generate(): Promise<void> {
        if (this.promptCtrl.invalid) {
            this.promptCtrl.markAsTouched();
            return;
        }

        if (this.data?.kpiId == null) {
            this.setInlineAlert('error', 'KPI id is missing.');
            return;
        }

        const prompt = this.promptCtrl.value.trim();
        if (!prompt) return;

        this.isGenerating = true;
        this.showAlert = false;
        this.cdr.markForCheck();

        try {
            const payload = {
                kpi_id: this.data.kpiId,
                prompt,
            };

            const res = await firstValueFrom(
                this.http.put(
                    `${environment.apiLink}kpi-logic/update-kpi-with-ai`,
                    payload
                )
            );

            const pair = this.extractGeneratedPair(res);
            if (pair) {
                this.oldKpi = pair.oldKpi;
                this.generatedKpi = pair.generatedKpi;
            } else {
                const generatedOnly = this.extractGeneratedOnly(res);
                if (!generatedOnly) {
                    this.setInlineAlert(
                        'error',
                        'Generated KPI data was not found in API response.'
                    );
                    return;
                }
                // Keep existing current KPI as old and set generated candidate.
                this.generatedKpi = generatedOnly;
            }
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isGenerating = false;
            this.cdr.markForCheck();
        }
    }

    isFieldChanged(field: 'kpi_name' | 'calculation' | 'description' | 'columns_used'): boolean {
        if (!this.oldKpi || !this.generatedKpi) return false;
        if (field === 'columns_used') {
            const oldValue = (this.oldKpi.columns_used || []).join(',');
            const newValue = (this.generatedKpi.columns_used || []).join(',');
            return oldValue !== newValue;
        }
        return String(this.oldKpi[field] ?? '') !== String(this.generatedKpi[field] ?? '');
    }

    async saveGeneratedKpi(): Promise<void> {
        if (!this.generatedKpi) {
            this.setInlineAlert('error', 'Generate KPI first before saving.');
            return;
        }

        this.isSaving = true;
        this.showAlert = false;
        this.cdr.markForCheck();
        try {
            const payload = {
                kpi_id: this.generatedKpi.id ?? this.data.kpiId,
                level: this.generatedKpi.level ?? 0,
                kpi_name: this.generatedKpi.kpi_name,
                calculation: this.generatedKpi.calculation,
                columns_used: this.generatedKpi.columns_used ?? [],
                description: this.generatedKpi.description,
            };
            const res = await firstValueFrom(
                this.http.put(
                    `${environment.apiLink}kpi-logic/save-generated-kpi`,
                    payload
                )
            );

            const saved = this.extractSavedKpi(res) ?? [this.generatedKpi];
            this.dialogRef.close(saved);
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isSaving = false;
            this.cdr.markForCheck();
        }
    }

    async validateKpi(): Promise<void> {
        if (this.data?.kpiId == null) {
            this.setInlineAlert('error', 'KPI id is missing.');
            return;
        }

        await this.executeValidationRequest({
            kpi_id: this.data.kpiId,
        });
    }

    async validateUpdatedKpiData(): Promise<void> {
        if (!this.generatedKpi) {
            this.setInlineAlert('error', 'Generate KPI first before validating updated KPI data.');
            return;
        }

        const payload = {
            generated_kpi: this.toGeneratedKpiPayload(this.generatedKpi),
            ...this.buildValidationContextPayload(),
        };

        await this.executeValidationRequest(payload);
    }

    private buildValidationContextPayload(): {
        isExternal: boolean;
        tableId: number;
        externalTableId: number;
    } {
        const isExternal = !!this.data?.isExternal;
        return {
            isExternal,
            tableId: isExternal ? 0 : Number(this.data?.tableId ?? 0),
            externalTableId: isExternal
                ? Number(this.data?.externalTableId ?? 0)
                : 0,
        };
    }

    private toGeneratedKpiPayload(kpi: KpiLogicItem): GeneratedKpiPayload {
        return {
            level: Number(kpi.level ?? 0),
            kpi_name: String(kpi.kpi_name ?? ''),
            calculation: String(kpi.calculation ?? ''),
            columns_used: Array.isArray(kpi.columns_used)
                ? kpi.columns_used.map(v => String(v))
                : [],
            description: String(kpi.description ?? ''),
        };
    }

    private async executeValidationRequest(payload: any): Promise<void> {
        this.isValidating = true;
        this.showAlert = false;
        this.validationPreview = null;
        this.cdr.markForCheck();
        try {
            const res: any = await firstValueFrom(
                this.http.post(`${environment.apiLink}kpi-logic/validate-kpi`, payload)
            );
            const sql = String(res?.data?.data?.sql ?? '');
            const rows = Array.isArray(res?.data?.data?.rows)
                ? (res.data.data.rows as Record<string, unknown>[])
                : [];

            this.validationPreview = { sql, rows };
            if (sql) {
                const pretty = this.formatSqlSafely(sql);
                const html = Prism.highlight(pretty, Prism.languages.sql, 'sql');
                this.highlightedValidationSql =
                    this.sanitizer.bypassSecurityTrustHtml(html);
            } else {
                this.highlightedValidationSql = '';
            }
            this.setInlineAlert('success', 'KPI validated successfully');
        } catch (e: any) {
            this.setInlineAlert('error', this.getErrorMessage(e));
        } finally {
            this.isValidating = false;
            this.cdr.markForCheck();
        }
    }
}
